import React from 'react';
import { CheckIcon } from 'lucide-react';
import { foundingBenefits, foundingPerks, foundingPlans, foundingCommitments, foundingCommitmentsIntro, foundingRateNote } from '../../data/foundingOffer';
import { PlanPrice } from './PlanPrice';
export function FoundingOffer() {
  return <div className="space-y-20 md:space-y-24" data-id="element-505" data-mp="3jhftswxn9qfd">
      {/* What founding members get */}
      <section aria-labelledby="founding-benefits-heading" data-id="element-506" data-mp="kz0syfhewzpk">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-4" data-id="element-507" data-mp="kyimyr4y774g">
          Your founding offer
        </p>
        <h2 id="founding-benefits-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-508" data-mp="b97g7b5jaejq">
          What you have — permanently.
        </h2>

        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-id="element-509" data-mp="13piarp0piaw5">
          {foundingBenefits.map(benefit => <div key={benefit.title} className="rounded-2xl border border-purple-200 border-t-2 border-t-purple-600 bg-white px-6 py-5" data-id="element-510" data-mp="yyjp09wy8kli">
              <h3 className="font-serif text-lg text-charcoal" data-id="element-511" data-mp="365t8e3ajq8wr">
                {benefit.title}
              </h3>
              <p className="mt-2 text-sm text-charcoal/70 leading-relaxed" data-id="element-512" data-mp="2rhexd52z7e54">
                {benefit.description}
              </p>
            </div>)}
        </div>

        <div className="mt-6 rounded-2xl border-l-2 border-purple-600 bg-purple-50 px-6 py-6" data-id="element-513" data-mp="2wdnaye1iyza6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-800 mb-4" data-id="element-514" data-mp="2iil3vnkxz3vs">
            What you keep — forever
          </p>
          <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2" data-id="element-515" data-mp="28j2nfh48q08h">
            {foundingPerks.map(perk => <li key={perk} className="flex items-start gap-2.5 text-sm text-charcoal/80 leading-relaxed" data-id="element-516" data-mp="2dhx7snhuvwup">
                <CheckIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-517" data-mp="bpiobjznykc6" />
                {perk}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Plans */}
      <section aria-labelledby="founding-plans-heading" data-id="element-518" data-mp="5bljzx2azaiv">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-4" data-id="element-519" data-mp="bgzlvy9xvnd">
          Choose your plan
        </p>
        <h2 id="founding-plans-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-520" data-mp="3ritl2n66wv93">
          Your founding rate — locked in.
        </h2>
        <p className="mt-4 max-w-2xl text-base text-charcoal/70 leading-relaxed" data-id="element-521" data-mp="o238fpg3rjyf">
          Two months free to start. Beginning Month 3, you're billed at your
          founding rate — locked in for life. For Bloom, Thrive, and Collective
          tiers, each practitioner on the plan must be individually verified
          before their profile is activated.
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-id="element-522" data-mp="16y2ukjmefta3">
          {foundingPlans.map(plan => <div key={plan.name} className={`relative flex flex-col rounded-2xl px-6 py-6 ${plan.highlighted ? 'border-2 border-purple-600 bg-purple-50' : 'border border-purple-200 bg-white'}`} data-id="element-523" data-mp="1f934s6lii02x">
              {plan.highlighted && <span className="absolute -top-3 left-6 rounded-full bg-purple-700 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white" data-id="element-524" data-mp="1y6co7nqu7dk6">
                  Most popular
                </span>}
              <h3 className="font-serif text-2xl text-charcoal" data-id="element-525" data-mp="26yenhip66nsv">{plan.name}</h3>
              <p className="mt-1 text-sm text-charcoal/60" data-id="element-526" data-mp="2bxhsx492aw1d">{plan.seats}</p>
              <div className="mt-5" data-id="element-527" data-mp="2bzlw1jz4zr0l">
                <PlanPrice price={plan.price} priceSuffix={plan.priceSuffix} data-id="element-528" data-mp="2gxndy1kttkah" />
              </div>
              <ul className="mt-5 space-y-2.5" data-id="element-529" data-mp="2lv5p7ymbgbln">
                {plan.features.map(feature => <li key={feature} className="flex items-start gap-2.5 text-sm text-charcoal/80 leading-relaxed" data-id="element-530" data-mp="3imkb76nvgy2d">
                    <CheckIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-531" data-mp="3sd5eh7ehqqrm" />
                    {feature}
                  </li>)}
              </ul>
              {plan.note && <p className="mt-5 pt-4 border-t border-purple-200 text-sm italic text-charcoal/60 mt-auto" data-id="element-532" data-mp="2zqawqi9265hu">
                  {plan.note}
                </p>}
            </div>)}
        </div>
      </section>

      {/* Commitments */}
      <section aria-labelledby="founding-commitments-heading" data-id="element-533" data-mp="34mrp5onaj60q">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-4" data-id="element-534" data-mp="nye1vmp0ufiv">
          Your commitments
        </p>
        <h2 id="founding-commitments-heading" className="font-serif text-3xl md:text-4xl text-charcoal leading-tight" data-id="element-535" data-mp="jwsidihzf2zp">
          Help us build something exceptional.
        </h2>
        <p className="mt-4 max-w-2xl text-base text-charcoal/70 leading-relaxed" data-id="element-536" data-mp="wh8zxbwjm2vy">
          {foundingCommitmentsIntro}
        </p>

        <ol className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-537" data-mp="mhrw700upz2m">
          {foundingCommitments.map((commitment, index) => <li key={commitment.text} className="flex items-start gap-3 text-base text-charcoal/80 leading-relaxed" data-id="element-538" data-mp="1a6sy04f6ibyx" data-mpi={index}>
              <span aria-hidden="true" className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-700 text-[11px] font-semibold text-white" data-id="element-539" data-mp="1f8i91k2eipn0">
                {index + 1}
              </span>
              <span data-id="element-540" data-mp="1t5ez3jap432u">
                {commitment.text}
                {commitment.optional && <span className="ml-2 inline-block rounded-full border border-purple-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-purple-700 align-middle" data-id="element-541" data-mp="1xxov9h2elzdk">
                    Optional
                  </span>}
              </span>
            </li>)}
        </ol>

        <p className="mt-6 text-sm text-charcoal/55 leading-relaxed" data-id="element-542" data-mp="1yqe76fb2fhbg">
          *{foundingRateNote}
        </p>
      </section>
    </div>;
}